import React, { useLayoutEffect, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFluidPaymentsStore } from '@/features/cashier/fluid/fluidPaymentsStore';

import { FluidQuickDepositInfoEvents } from '@/features/cashier/fluid/constants';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';

import { selectLowestRemainingDepositLimit } from '@/store/selectors/limitsSelectors';
import { useParams } from 'react-router-dom';
import { selectShowFluidQuickDeposit } from '@/store/ui/selectors';
import { selectFluidConfig } from '@/store/selectors/combined.selectors';
import { getUserWallet } from '@/store/wallet/actions';
import { getBonuses } from '@/store/bonuses/actions';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

type FluidEventDetails<T = {}> = {
  message: string;
} & T;

interface FluidEvent {
  detail: FluidEventDetails;
}

const FluidQuickDeposit = ({ open }: { open: boolean }) => {
  const dispatch = useDispatch();
  const { activeBonuses } = useFluidPaymentsStore();
  const ref = useRef<any>(null);
  const depositLimit = useSelector(selectLowestRemainingDepositLimit);
  const fluidConfig = useSelector(selectFluidConfig);
  const showFluidQuickDeposit = useSelector(selectShowFluidQuickDeposit);
  const isRestricted = useRestrictedMode();
  const { gameId } = useParams();

  useLayoutEffect(() => {
    const fluidQuickDeposit = ref.current;

    if (fluidQuickDeposit) {
      const handleInfoEvent = (event: FluidEvent) => {
        if (!event.detail || !event.detail.message) return;

        switch (event.detail.message) {
          case FluidQuickDepositInfoEvents['deposit-cta-clicked']:
            triggerGTag(GTagEvents.fluid_quick_deposit_clicked, { gameId: parseInt(gameId ?? '') });
            dispatch(getUserWallet());
            dispatch(getBonuses());
            break;
          case FluidQuickDepositInfoEvents['error-cta-clicked']:
            triggerGTag(GTagEvents.fluid_quick_deposit_error);
            break;
        }
      };

      fluidQuickDeposit.addEventListener('fluid-info', handleInfoEvent);

      return () => {
        fluidQuickDeposit.removeEventListener('fluid-info', handleInfoEvent);
      };
    }
  }, [gameId]);

  // Deposits are disabled in restricted mode — never show the quick-deposit widget.
  if (!fluidConfig || !open || !showFluidQuickDeposit || isRestricted) return null;

  return (
    <div className="flex justify-center h-[60px]">
      <fluid-quick-deposit
        ref={ref}
        id="fluid-quick-deposit"
        operator-id={fluidConfig.operatorId}
        user-id={fluidConfig.userId}
        session-id={fluidConfig.sessionId}
        locale={fluidConfig.locale}
        country={fluidConfig.countryCode}
        currency={fluidConfig.currencyCode}
        bonuses={JSON.stringify(activeBonuses)}
        deposit-limit={depositLimit !== null ? depositLimit : ''}
        transaction="quick-deposit"
        open={open}
        z-index="100"
      ></fluid-quick-deposit>
    </div>
  );
};

export default FluidQuickDeposit;
